import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { assetsApi } from 'src/Services/API';
import { CreateInvitationsDto, InvitationDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';
import { trackInvitationSent } from 'src/Services/Mixpanel/events';

export const useAssetInviteUser = (
  assetId?: string,
  options?: UseMutationOptions<InvitationDto[], unknown, CreateInvitationsDto>
) => {
  const currentAssetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  const assetIdForRequest = assetId ?? currentAssetId;

  return useMutation<InvitationDto[], unknown, CreateInvitationsDto>(
    ['assetInviteUser', assetIdForRequest],
    async data => {
      if (!assetIdForRequest) {
        throw new Error('No asset selected');
      }

      const response = await assetsApi.assetsControllerCreateInvitations(assetIdForRequest, data);
      return response.data;
    },
    {
      ...options,
      onSettled: (data, error, variables, context) => {
        queryClient.invalidateQueries(['fetch-members', currentAssetId]);
        options?.onSettled?.(data, error, variables, context);
      },
      onSuccess: (data, variables, context) => {
        trackEvent('Invitation', 'invite', variables.emails.join(', '));
        trackInvitationSent({
          method: 'email',
          recipients_count: variables.emails.length,
          role: variables.preset,
        });
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};
